/**
 * Load something from the API, and know which of four things is true about it.
 *
 * Deliberately small, and deliberately not a data-fetching library. The whole app loads six
 * resources; a cache with invalidation rules would be more machinery than the thing it manages.
 *
 * The four states are explicit because collapsing them is how a screen starts lying. "Loading" and
 * "loaded, and empty" render very differently — the second is a real statement about a person's
 * workspace and the first is a statement about the network — and an app that shows an empty state
 * while a request is in flight has told somebody their knowledge base is empty when it is not.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "../api/errors";

export interface Resource<T> {
  data: T | null;
  error: ApiError | null;
  /** True until the first load settles. Stays false during a refresh, so the list does not flash. */
  loading: boolean;
  /** True while a reload is in flight over data that is already on screen. */
  refreshing: boolean;
  reload: () => void;
  /** Replace the local copy without a round trip, after a write whose response is the new truth. */
  set: (next: T) => void;
}

export function useResource<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [nonce, setNonce] = useState(0);
  // Read inside the effect rather than listed as a dependency: reloading must not itself be a
  // reason to reload.
  const hasData = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    if (hasData.current) setRefreshing(true);
    else setLoading(true);

    load(controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return;
        hasData.current = true;
        setData(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setError(
          cause instanceof ApiError
            ? cause
            : new ApiError("Something went wrong loading this.", {
                code: "internal_error",
                status: 0,
              }),
        );
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
        setRefreshing(false);
      });

    return () => {
      controller.abort();
    };
    // `load` is recreated every render by design; `deps` is what the caller says matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  const set = useCallback((next: T) => {
    hasData.current = true;
    setData(next);
    setError(null);
  }, []);

  return { data, error, loading, refreshing, reload, set };
}
